import React from 'react';
import { computed, observable, action, makeObservable } from 'mobx';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { DateTime } from 'src_common/utils/time/time';
import { Messages } from 'src/domains/layouts/webview/components/Messages/Messages';
import { getRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { useCommon } from 'src/domains/common/Common';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { validateAmountNotRequired } from 'src/domains/players/webview/components/ValidatorsNew';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';

export class RollingNetDepositLimitItemState {
    @observable public refAmountInput: HTMLInputElement | null;

    public limitAmount: FormInputState<string, Amount | undefined>;

    public constructor(
        private readonly configComponents: ConfigComponents,
        //public readonly usersState: UsersState,
        private readonly selectedCurrency: CurrencyType,
        private readonly clearInfoMessage: () => void,
        public readonly getLimits: () => getRollingLimits.LimitsObjectSchemaType | undefined,
        private readonly getConfigMaxLimitPence: () => number | undefined
    ) {
        makeObservable(this);
        this.refAmountInput = null;

        this.limitAmount = FormInputState.new('').map(validateAmountNotRequired);
    }

    public setAmountInputRef = (node: HTMLInputElement | null): void => {
        this.refAmountInput = node;
    };

    public onBlur = (): void => {
        const amount = parseFloat(this.limitAmount.value);
        if (isNaN(amount) === true || amount === 0) {
            return this.limitAmount.setValue('');
        }
        return this.limitAmount.setValue(amount.toFixed(2));
    };

    @action public onChangeInput = (event: React.SyntheticEvent<HTMLInputElement>): void => {
        const newValue = event.currentTarget.value;
        this.clearInfoMessage();
        this.limitAmount.setValue(newValue);
    };

    @computed public get activeLimit(): string {
        const activeValue = this.getLimits()?.activeLimitValue;
        return activeValue === undefined
            ? 'Not set'
            : this.configComponents.precision
                  .newFromOld(activeValue)
                  .format(this.selectedCurrency, { withoutCurrencySymbol: true });
    }

    @action public resetValue = (): void => {
        this.limitAmount.reset();
    };

    @computed public get depositLimitInputCeilingPence(): number | undefined {
        return this.getConfigMaxLimitPence();
    }

    @computed public get formattedDepositLimitCeiling(): string {
        const ceiling = this.depositLimitInputCeilingPence;
        return ceiling === undefined
            ? this.configComponents.precision.newFromAnything(0).format(this.selectedCurrency)
            : this.configComponents.precision.newFromOld(ceiling).format(this.selectedCurrency);
    }

    @computed public get errorMaxLimitExceeded(): boolean {
        const ceiling = this.depositLimitInputCeilingPence;
        const currentValue =
            this.limitAmount.result.value.type === 'ok' ? this.limitAmount.result.value.data?.multiply(100) : undefined;
        const enteredPence = parseInt(currentValue?.value ?? '0', 10);
        return ceiling !== undefined && enteredPence > ceiling;
    }

    @computed public get isError(): boolean {
        return this.limitAmount.result.value.type === 'error' || this.errorMaxLimitExceeded;
    }
}

const pendingMessage = (
    configComponents: ConfigComponents,
    currency: CurrencyType,
    amount: number,
    lastUpdateDate: string
): JSX.Element | undefined => {
    const until = DateTime.from(lastUpdateDate)?.addDays(1).format('HH:mm DD-MM-YYYY');

    if (amount === 0 || until === undefined) {
        return undefined;
    }

    const next = configComponents.precision.newFromAnything(amount).format(currency);

    return (
        <I18n
            langKey='signup-journey.rolling-net-deposit-limits.pending-new-limit'
            defaultText='New limit of {next} will be available at {until}'
            params={{ next, until }}
        />
    );
};

const renderPending = (
    configComponents: ConfigComponents,
    currency: CurrencyType,
    limits: getRollingLimits.LimitsObjectSchemaType | undefined
): React.ReactElement | null => {
    const pendingLimitValue = limits?.pendingLimitValue;
    const pendingRequestedAt = limits?.pendingRequestedAt;

    if (
        pendingLimitValue === null ||
        pendingLimitValue === undefined ||
        pendingRequestedAt === null ||
        pendingRequestedAt === undefined
    ) {
        return null;
    }

    const message = pendingMessage(configComponents, currency, pendingLimitValue, pendingRequestedAt);

    return (
        <Messages
            type='success'
            message={message}
            dataTest='limit-set-message'
        />
    );
};

interface PropsType {
    state: RollingNetDepositLimitItemState;
    label: React.ReactElement;
    currency: CurrencyType;
    dataTest?: string;
}

export const TopUpLimitsProcedureItem = observer('TopUpLimitsItem', (props: PropsType) => {
    const { state, label, currency, dataTest } = props;
    const common = useCommon();
    const configComponents = ConfigComponents.get(common);

    return (
        <>
            <Input
                type='text'
                maxLength={10}
                label={label}
                currency={moneySymbol(currency)}
                placeholder={state.activeLimit}
                state={state.limitAmount}
                inputRef={state.setAmountInputRef}
                onChange={state.onChangeInput}
                colorTheme='dark'
                onBlur={state.onBlur}
                dataTest={dataTest}
            />
            {renderPending(configComponents, currency, state.getLimits())}
            {state.errorMaxLimitExceeded ? (
                <Messages
                    type='error'
                    message={
                        <I18n
                            langKey='signup-journey.rolling-net-deposit-limits.max-limit-input'
                            defaultText='Please set limit below {maxLimitValue}'
                            params={{
                                maxLimitValue: state.formattedDepositLimitCeiling,
                            }}
                        />
                    }
                    dataTest='limit-set-message'
                />
            ) : null}
        </>
    );
});
